import React, { useEffect, useState, useCallback } from "react";
import api, { assetUrl } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
    Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandItem } from "@/components/ui/command";
import {
    Plus, Edit, Trash2, Truck, Download, ChevronRight, Eye, ChevronsUpDown, Check,
    CheckCircle2, XCircle, FileText, Loader2,
} from "lucide-react";

const VEHICLE_TYPES = [
    { value: "camion", label: "Camión" },
    { value: "panel", label: "Panel" },
    { value: "pickup", label: "Pickup" },
    { value: "furgon", label: "Furgón" },
    { value: "cisterna", label: "Cisterna" },
    { value: "otro", label: "Otro" },
];

const TYPE_LABEL = Object.fromEntries(VEHICLE_TYPES.map((v) => [v.value, v.label]));

const emptyVehicle = {
    plate_number: "", brand: "", tonnage: "", vehicle_type: "camion",
};

function TransportClientCombobox({ clients, value, onChange }) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const selected = clients.find((c) => c.id === value);
    const label = selected ? `${selected.name}${selected.trade_name ? ` — ${selected.trade_name}` : ""}` : "";
    const filtered = clients.filter((c) => {
        if (!search) return true;
        const q = search.toLowerCase();
        return c.name?.toLowerCase().includes(q) || c.trade_name?.toLowerCase().includes(q) || c.client_code?.toLowerCase().includes(q);
    });
    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button variant="outline" role="combobox" aria-expanded={open} className="max-w-md w-full justify-between font-normal truncate" data-testid="transport-client-select">
                    <span className="truncate">{label || "Cliente facturación..."}</span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[340px] p-0" align="start">
                <Command shouldFilter={false}>
                    <CommandInput placeholder="Buscar cliente..." value={search} onValueChange={setSearch} data-testid="transport-client-search" />
                    <CommandList>
                        <CommandEmpty>Sin resultados</CommandEmpty>
                        {filtered.map((c) => (
                            <CommandItem key={c.id} value={c.id} onSelect={() => { onChange(c.id); setOpen(false); setSearch(""); }}>
                                <Check className={`mr-2 h-4 w-4 ${value === c.id ? "opacity-100" : "opacity-0"}`} />
                                <div className="flex flex-col min-w-0">
                                    <span className="text-sm truncate">{c.name}</span>
                                    {c.trade_name && <span className="text-xs text-muted-foreground truncate">{c.trade_name}</span>}
                                </div>
                            </CommandItem>
                        ))}
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}

function TransportBranchCombobox({ branches, value, onChange }) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const selected = branches.find((b) => b.id === value);
    const label = selected ? `${selected.name}${selected.odoo_contact_code ? ` (${selected.odoo_contact_code})` : ""}` : "";
    const filtered = branches.filter((b) => {
        if (!search) return true;
        const q = search.toLowerCase();
        return b.name?.toLowerCase().includes(q) || b.odoo_contact_code?.toLowerCase().includes(q) || b.address?.toLowerCase().includes(q);
    });
    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button variant="outline" role="combobox" aria-expanded={open} className="max-w-md w-full justify-between font-normal truncate" data-testid="transport-branch-select">
                    <span className="truncate">{label || "Cliente servicio..."}</span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[340px] p-0" align="start">
                <Command shouldFilter={false}>
                    <CommandInput placeholder="Buscar sucursal..." value={search} onValueChange={setSearch} data-testid="transport-branch-search" />
                    <CommandList>
                        <CommandEmpty>Sin sucursales</CommandEmpty>
                        {filtered.map((b) => (
                            <CommandItem key={b.id} value={b.id} onSelect={() => { onChange(b.id); setOpen(false); setSearch(""); }}>
                                <Check className={`mr-2 h-4 w-4 ${value === b.id ? "opacity-100" : "opacity-0"}`} />
                                <div className="flex flex-col min-w-0">
                                    <span className="text-sm truncate">{b.name}</span>
                                    {b.odoo_contact_code && <span className="text-xs text-muted-foreground truncate">{b.odoo_contact_code}{b.address ? ` · ${b.address}` : ""}</span>}
                                </div>
                            </CommandItem>
                        ))}
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}

export default function AdminTransportes() {
    const [clients, setClients] = useState([]);
    const [selectedClientId, setSelectedClientId] = useState("");
    const [branches, setBranches] = useState([]);
    const [selectedBranchId, setSelectedBranchId] = useState("");
    const [vehicles, setVehicles] = useState([]);
    const [services, setServices] = useState([]);
    const [showVehicleDialog, setShowVehicleDialog] = useState(false);
    const [editingVehicle, setEditingVehicle] = useState(null);
    const [vForm, setVForm] = useState(emptyVehicle);
    const [tab, setTab] = useState("fleet"); // fleet | history
    const [selectedService, setSelectedService] = useState(null);
    const [certs, setCerts] = useState({});
    const [certLoading, setCertLoading] = useState(null);

    useEffect(() => {
        if (!selectedService) { setCerts({}); return; }
        api.get(`/transport/services/${selectedService.id}/certificates`)
            .then((r) => setCerts(r.data || {}))
            .catch(() => setCerts({}));
    }, [selectedService]);

    const generateCert = async (vehicleId) => {
        if (!selectedService) return;
        setCertLoading(vehicleId);
        try {
            const r = await api.post(`/transport/services/${selectedService.id}/certificate`, { vehicle_id: vehicleId });
            setCerts((c) => ({ ...c, [vehicleId]: r.data.pdf_url }));
            window.open(assetUrl(r.data.pdf_url), "_blank");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error al generar certificado");
        } finally {
            setCertLoading(null);
        }
    };

    useEffect(() => {
        api.get("/clients").then((r) => setClients(r.data));
    }, []);

    // Load branches when client changes
    useEffect(() => {
        if (!selectedClientId) { setBranches([]); setSelectedBranchId(""); return; }
        setSelectedBranchId("");
        api.get(`/branches?client_id=${selectedClientId}`).then((r) => {
            setBranches(r.data || []);
            if (r.data?.[0]) setSelectedBranchId(r.data[0].id);
        }).catch(() => setBranches([]));
    }, [selectedClientId]);

    const loadVehicles = useCallback(async () => {
        if (!selectedClientId) return;
        const r = await api.get(`/transport/vehicles?client_id=${selectedClientId}`);
        setVehicles(r.data);
    }, [selectedClientId]);

    const loadServices = useCallback(async () => {
        if (!selectedClientId) return;
        const r = await api.get(`/transport/services?client_id=${selectedClientId}`);
        setServices(r.data);
    }, [selectedClientId]);

    useEffect(() => {
        if (selectedClientId) {
            loadVehicles();
            loadServices();
        } else {
            setVehicles([]);
            setServices([]);
        }
    }, [selectedClientId, loadVehicles, loadServices]);

    const openCreate = () => {
        setEditingVehicle(null);
        setVForm(emptyVehicle);
        setShowVehicleDialog(true);
    };
    const openEdit = (v) => {
        setEditingVehicle(v);
        setVForm({
            plate_number: v.plate_number,
            brand: v.brand || "",
            tonnage: v.tonnage || "",
            vehicle_type: v.vehicle_type || "camion",
        });
        setShowVehicleDialog(true);
    };

    const submitVehicle = async () => {
        try {
            const payload = { ...vForm, client_id: selectedClientId };
            if (editingVehicle) {
                await api.put(`/transport/vehicles/${editingVehicle.id}`, payload);
            } else {
                await api.post("/transport/vehicles", payload);
            }
            toast.success(editingVehicle ? "Vehículo actualizado" : "Vehículo creado");
            setShowVehicleDialog(false);
            loadVehicles();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error");
        }
    };

    const removeVehicle = async (v) => {
        if (!window.confirm(`¿Eliminar vehículo ${v.plate_number}?`)) return;
        try {
            await api.delete(`/transport/vehicles/${v.id}`);
            toast.success("Vehículo eliminado");
            loadVehicles();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error");
        }
    };

    const selectedClient = clients.find((c) => c.id === selectedClientId);

    return (
        <div data-testid="admin-transportes">
            <div className="flex items-center gap-3 mb-4">
                <Truck className="w-6 h-6 text-[hsl(var(--trulyn-navy))]" />
                <h1 className="text-2xl font-semibold" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>
                    Control de Transportes
                </h1>
            </div>

            {/* Client selector */}
            <Card className="p-4 mb-4" data-testid="transport-client-selector">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 block">
                    Seleccionar cliente
                </Label>
                <div className="flex flex-col sm:flex-row gap-3">
                    <TransportClientCombobox clients={clients} value={selectedClientId} onChange={setSelectedClientId} />
                    {selectedClientId && branches.length > 0 && (
                        <TransportBranchCombobox branches={branches} value={selectedBranchId} onChange={setSelectedBranchId} />
                    )}
                </div>
            </Card>

            {selectedClientId && (
                <>
                    {/* Tabs */}
                    <div className="flex gap-1 mb-4">
                        {[
                            { key: "fleet", label: "Flota de Vehículos", count: vehicles.length },
                            { key: "history", label: "Historial de Servicios", count: services.length },
                        ].map((t) => (
                            <Button
                                key={t.key}
                                variant={tab === t.key ? "default" : "outline"}
                                size="sm"
                                onClick={() => setTab(t.key)}
                                className={tab === t.key ? "bg-[hsl(var(--trulyn-navy))] text-white" : ""}
                                data-testid={`transport-tab-${t.key}`}
                            >
                                {t.label}
                                <Badge variant="secondary" className="ml-1.5 text-[10px]">{t.count}</Badge>
                            </Button>
                        ))}
                    </div>

                    {tab === "fleet" && (
                        <>
                            <div className="flex items-center justify-between mb-3">
                                <div className="text-sm text-muted-foreground">
                                    Flota de <span className="font-semibold text-foreground">{selectedClient?.name}</span>
                                </div>
                                <Button
                                    onClick={openCreate}
                                    size="sm"
                                    className="bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
                                    data-testid="transport-add-vehicle"
                                >
                                    <Plus className="w-4 h-4 mr-1" /> Agregar vehículo
                                </Button>
                            </div>
                            <Card data-testid="transport-vehicles-table">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Placa</TableHead>
                                            <TableHead>Tipo</TableHead>
                                            <TableHead>Marca</TableHead>
                                            <TableHead>Tonelaje</TableHead>
                                            <TableHead>Estado</TableHead>
                                            <TableHead className="text-right">Acciones</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {vehicles.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={6} className="text-center text-muted-foreground py-10">
                                                    Sin vehículos registrados
                                                </TableCell>
                                            </TableRow>
                                        )}
                                        {vehicles.map((v) => (
                                            <TableRow key={v.id} data-testid={`vehicle-row-${v.id}`}>
                                                <TableCell className="font-mono font-semibold">{v.plate_number}</TableCell>
                                                <TableCell>{TYPE_LABEL[v.vehicle_type] || v.vehicle_type}</TableCell>
                                                <TableCell>{v.brand || "—"}</TableCell>
                                                <TableCell>{v.tonnage || "—"}</TableCell>
                                                <TableCell>
                                                    <Badge variant={v.is_active ? "default" : "secondary"} className={v.is_active ? "bg-green-100 text-green-800 border-green-200" : ""}>
                                                        {v.is_active ? "Activo" : "Inactivo"}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell className="text-right space-x-1">
                                                    <Button variant="ghost" size="icon" onClick={() => openEdit(v)} data-testid={`vehicle-edit-${v.id}`}>
                                                        <Edit className="w-4 h-4" />
                                                    </Button>
                                                    <Button variant="ghost" size="icon" onClick={() => removeVehicle(v)} data-testid={`vehicle-delete-${v.id}`}>
                                                        <Trash2 className="w-4 h-4 text-red-600" />
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Card>
                        </>
                    )}

                    {tab === "history" && (
                        <Card data-testid="transport-services-table">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Fecha</TableHead>
                                        <TableHead>Vehículos</TableHead>
                                        <TableHead>Tratados</TableHead>
                                        <TableHead>Realizado por</TableHead>
                                        <TableHead>Aceptado por</TableHead>
                                        <TableHead className="text-right">Acciones</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {services.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={6} className="text-center text-muted-foreground py-10">
                                                Sin servicios de transporte registrados
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {services.map((s) => {
                                        const total = s.treatments?.length || 0;
                                        const treated = s.treatments?.filter((t) => t.service_done).length || 0;
                                        return (
                                            <TableRow key={s.id} data-testid={`transport-service-row-${s.id}`} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedService(s)}>
                                                <TableCell className="font-mono">{s.service_date}</TableCell>
                                                <TableCell>{total}</TableCell>
                                                <TableCell>
                                                    <span className="font-semibold text-green-700">{treated}</span>
                                                    <span className="text-muted-foreground">/{total}</span>
                                                </TableCell>
                                                <TableCell>{s.performed_by || "—"}</TableCell>
                                                <TableCell>{s.accepted_by || "—"}</TableCell>
                                                <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                                                    <div className="flex items-center justify-end gap-1">
                                                        <Button variant="ghost" size="icon" onClick={() => setSelectedService(s)} data-testid={`transport-detail-${s.id}`}>
                                                            <Eye className="w-4 h-4" />
                                                        </Button>
                                                        {s.pdf_url && (
                                                            <a href={assetUrl(s.pdf_url)} target="_blank" rel="noreferrer">
                                                                <Button variant="ghost" size="icon" data-testid={`transport-pdf-${s.id}`}>
                                                                    <Download className="w-4 h-4" />
                                                                </Button>
                                                            </a>
                                                        )}
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </Card>
                    )}
                </>
            )}

            {/* Service Detail Dialog (with per-vehicle certificates) */}
            <Dialog open={!!selectedService} onOpenChange={() => setSelectedService(null)}>
                <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
                    {selectedService && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="flex items-center gap-2">
                                    <Truck className="w-5 h-5 text-[#CC0000]" />
                                    Detalle del Servicio — {selectedService.service_date}
                                </DialogTitle>
                            </DialogHeader>

                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Placa</TableHead>
                                        <TableHead>Tipo</TableHead>
                                        <TableHead>Marca</TableHead>
                                        <TableHead>Servicio</TableHead>
                                        <TableHead>Productos</TableHead>
                                        <TableHead>Obs.</TableHead>
                                        <TableHead className="text-center">Certificado</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {(selectedService.treatments || []).map((t, i) => (
                                        <TableRow key={i}>
                                            <TableCell className="font-mono font-semibold">{t.plate_number}</TableCell>
                                            <TableCell className="text-sm">{TYPE_LABEL[t.vehicle_type] || t.vehicle_type}</TableCell>
                                            <TableCell className="text-sm">{t.brand || "—"}</TableCell>
                                            <TableCell>
                                                {t.service_done ? (
                                                    <div className="flex items-center gap-1 text-green-700">
                                                        <CheckCircle2 className="w-4 h-4" />
                                                        <span className="text-xs font-semibold">SI</span>
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center gap-1 text-orange-600">
                                                        <XCircle className="w-4 h-4" />
                                                        <span className="text-xs font-semibold">NO</span>
                                                    </div>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-xs">
                                                {t.products_applied?.length > 0
                                                    ? t.products_applied.map((p) => p.product_name).join(", ")
                                                    : "—"}
                                            </TableCell>
                                            <TableCell className="text-xs text-muted-foreground max-w-[120px] truncate">
                                                {t.observations || "—"}
                                            </TableCell>
                                            <TableCell className="text-center">
                                                {!t.service_done ? (
                                                    <span className="text-xs text-muted-foreground">—</span>
                                                ) : certs[t.vehicle_id] ? (
                                                    <a href={assetUrl(certs[t.vehicle_id])} target="_blank" rel="noreferrer">
                                                        <Button variant="outline" size="sm" className="h-7 text-[11px] gap-1" data-testid={`admin-cert-download-${t.vehicle_id}`}>
                                                            <Download className="w-3 h-3" /> Ver
                                                        </Button>
                                                    </a>
                                                ) : (
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        className="h-7 text-[11px] gap-1 border-[#CC0000]/40 text-[#CC0000] hover:bg-[#CC0000]/5"
                                                        disabled={certLoading === t.vehicle_id}
                                                        onClick={() => generateCert(t.vehicle_id)}
                                                        data-testid={`admin-cert-generate-${t.vehicle_id}`}
                                                    >
                                                        {certLoading === t.vehicle_id
                                                            ? <Loader2 className="w-3 h-3 animate-spin" />
                                                            : <FileText className="w-3 h-3" />}
                                                        Generar
                                                    </Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>

                            {selectedService.general_observations && (
                                <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                                    <p className="text-xs uppercase tracking-wider text-amber-700 font-semibold mb-1">Observaciones generales</p>
                                    <p className="text-sm">{selectedService.general_observations}</p>
                                </div>
                            )}
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* Vehicle Create/Edit Dialog */}
            <Dialog open={showVehicleDialog} onOpenChange={setShowVehicleDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {editingVehicle ? "Editar vehículo" : "Nuevo vehículo"}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label>Número de placa *</Label>
                            <Input
                                value={vForm.plate_number}
                                onChange={(e) => setVForm((f) => ({ ...f, plate_number: e.target.value }))}
                                placeholder="Ej: P 154 C6"
                                data-testid="vehicle-form-plate"
                            />
                        </div>
                        <div>
                            <Label>Tipo de vehículo</Label>
                            <Select
                                value={vForm.vehicle_type}
                                onValueChange={(v) => setVForm((f) => ({ ...f, vehicle_type: v }))}
                            >
                                <SelectTrigger data-testid="vehicle-form-type">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {VEHICLE_TYPES.map((t) => (
                                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Marca</Label>
                            <Input
                                value={vForm.brand}
                                onChange={(e) => setVForm((f) => ({ ...f, brand: e.target.value }))}
                                placeholder="Ej: Chevrolet"
                                data-testid="vehicle-form-brand"
                            />
                        </div>
                        <div>
                            <Label>Tonelaje</Label>
                            <Input
                                value={vForm.tonnage}
                                onChange={(e) => setVForm((f) => ({ ...f, tonnage: e.target.value }))}
                                placeholder="Ej: 5 ton"
                                data-testid="vehicle-form-tonnage"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowVehicleDialog(false)}>Cancelar</Button>
                        <Button
                            onClick={submitVehicle}
                            disabled={!vForm.plate_number.trim()}
                            className="bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]"
                            data-testid="vehicle-form-submit"
                        >
                            {editingVehicle ? "Guardar" : "Crear"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
